/**
 * MonthlyKpisTab
 * ---------------
 * Tab interno del Dashboard (Phase 21.1) con la vista de dirección: KPIs
 * mensuales del consultorio. Solo visible para roles admin/dirección
 * (gating por `mgmt` en el padre).
 *
 * Componentes:
 *  - Selector de mes (input type="month").
 *  - 4 KPI cards (total, ticket medio, nº cobros, nº cirugías).
 *  - Gráfica de barras por método de pago (recharts).
 *  - Tabla de mix por tipo de cirugía.
 *
 * Consumidor del endpoint: GET /api/dashboard/monthly-kpis?month=YYYY-MM
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api, formatEUR } from '../lib/api';
import { MiniStat, SectionCard } from './Common';
import { Receipt, TrendingUp, Users, Scissors, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from 'recharts';

const METHOD_LABELS = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  payment_link: 'Enlace',
  other: 'Otros',
};

const METHOD_COLORS = {
  cash: '#2E6E5E',
  card: '#2E6E5E',
  transfer: '#4AABE0',
  payment_link: '#1D3F6E',
  other: '#A3B5C7',
};

// Code Quality Phase 23.5 — Constantes estáticas de los charts de Recharts
// extraídas fuera del componente para evitar la creación de objetos nuevos
// en cada render (que causaría re-renders innecesarios en las gráficas).
const CHART_MARGIN = { top: 10, right: 16, left: 0, bottom: 10 };
const X_AXIS_TICK = { fontSize: 12, fill: '#2E6E5E' };
const Y_AXIS_TICK = { fontSize: 11, fill: '#2E6E5E' };
const TOOLTIP_CURSOR = { fill: 'rgba(46,110,94,0.08)' };
// Superficie de tema (clara/oscura) para el tooltip de Recharts (2026-07-17).
const TOOLTIP_CONTENT_STYLE = { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' };
const BAR_RADIUS = [6, 6, 0, 0];
const Y_TICK_FORMATTER = (v) => `${(v / 1000).toFixed(0)}k`;
const TOOLTIP_FORMATTER = (val) => formatEUR(val);
const TOOLTIP_LABEL_FORMATTER = (label) => label;

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function buildMethodSeries(byMethod) {
  if (!byMethod) return [];
  return Object.keys(METHOD_LABELS).map((key) => {
    const bucket = byMethod[key] || { total: 0, count: 0 };
    return {
      method: METHOD_LABELS[key],
      methodKey: key,
      total: Number(bucket.total || 0),
      count: Number(bucket.count || 0),
    };
  });
}

export default function MonthlyKpisTab() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/dashboard/monthly-kpis?month=${month}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const methodSeries = useMemo(() => buildMethodSeries(data?.cobros?.by_method), [data]);

  return (
    <div data-testid="monthly-kpis-tab">
      {/* Selector de mes */}
      <div className="cjo-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#13231E]">
          Mes a analizar
        </div>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          max={currentMonth()}
          className="px-3 py-1.5 rounded-md border border-border bg-white text-sm font-mono text-[#13231E] focus:outline-none focus:ring-2 focus:ring-[#2E6E5E]"
          data-testid="kpis-month-input"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-[#2E6E5E]" />}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="monthly-kpis-cards">
        <MiniStat
          label="Ingresos del mes"
          value={loading ? '—' : formatEUR(data?.cobros?.total || 0)}
          hint={`Base ${formatEUR(data?.cobros?.base || 0)} · IVA ${formatEUR(data?.cobros?.iva || 0)}`}
          icon={Receipt}
        />
        <MiniStat
          label="Ticket medio"
          value={loading ? '—' : formatEUR(data?.cobros?.ticket_medio || 0)}
          hint={`${data?.cobros?.count || 0} cobros en el mes`}
          icon={TrendingUp}
        />
        <MiniStat
          label="Cirugías registradas"
          value={loading ? '—' : (data?.surgeries?.count ?? 0)}
          hint={data?.surgeries?.count ? `${formatEUR(data?.surgeries?.total || 0)} facturado` : 'sin cirugías aún'}
          icon={Scissors}
        />
        <MiniStat
          label="Pacientes nuevos"
          value={loading ? '—' : (data?.new_patients ?? 0)}
          hint="creados este mes"
          icon={Users}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Gráfica por método */}
        <SectionCard
          title="Cobros por método de pago"
          className="lg:col-span-3"
          action={<div className="text-xs text-muted-foreground tabular-nums">{methodSeries.reduce((s, m) => s + m.count, 0)} cobros</div>}
        >
          {methodSeries.every(m => m.total === 0) ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Sin cobros en {month}.
            </div>
          ) : (
            <div className="w-full h-64" data-testid="kpis-method-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={methodSeries} margin={CHART_MARGIN}>
                  <XAxis dataKey="method" tick={X_AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={Y_AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={Y_TICK_FORMATTER}
                  />
                  <RTooltip
                    cursor={TOOLTIP_CURSOR}
                    formatter={TOOLTIP_FORMATTER}
                    labelFormatter={TOOLTIP_LABEL_FORMATTER}
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                  />
                  <Bar dataKey="total" radius={BAR_RADIUS}>
                    {methodSeries.map((row) => (
                      <Cell key={row.methodKey} fill={METHOD_COLORS[row.methodKey] || '#2E6E5E'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
            {methodSeries.map((row) => (
              <div key={row.methodKey} className="rounded-md border border-border p-2 text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{row.method}</div>
                <div className="text-sm font-semibold text-[#13231E] tabular-nums">{formatEUR(row.total)}</div>
                <div className="text-[10px] text-muted-foreground">{row.count} cobros</div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Mix de cirugías */}
        <SectionCard title="Mix de cirugías (registro QX)" className="lg:col-span-2">
          {(!data?.surgeries?.mix?.length) ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Sin cirugías en {month}.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="kpis-surgery-mix">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="py-2 px-2">Tipo</th>
                    <th className="py-2 px-2 text-right">Nº</th>
                    <th className="py-2 px-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.surgeries.mix.map((row) => (
                    <tr key={row.surgery_type} className="border-t border-border">
                      <td className="py-2 px-2">{row.surgery_type}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{row.count}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold text-[#13231E]">
                        {formatEUR(row.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
